<template>
  <div :class="containerClasses" :role="role" :aria-label="ariaLabel">
    <!-- Spinner Loader -->
    <div v-if="variant === 'spinner'" :class="spinnerClasses">
      <svg 
        :class="sizeClasses"
        :style="customColor ? { color: customColor } : {}"
        class="animate-spin"
        fill="none"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </div>

    <!-- Dots Loader -->
    <div v-else-if="variant === 'dots'" :class="dotsContainerClasses">
      <div :class="dotClasses" :style="customColor ? { backgroundColor: customColor } : {}"></div>
      <div :class="dotClasses" :style="[customColor ? { backgroundColor: customColor } : {}, { animationDelay: '0.15s' }]"></div>
      <div :class="dotClasses" :style="[customColor ? { backgroundColor: customColor } : {}, { animationDelay: '0.3s' }]"></div>
    </div>

    <!-- Pulse Loader -->
    <div v-else-if="variant === 'pulse'" :class="pulseClasses" :style="customColor ? { backgroundColor: customColor } : {}">
    </div>

    <!-- Progress Bar -->
    <div v-else-if="variant === 'progress'" class="w-full">
      <div :class="progressBarContainerClasses">
        <div 
          :class="progressBarClasses"
          :style="{ 
            width: `${progress}%`, 
            backgroundColor: customColor || undefined 
          }"
          :aria-valuenow="progress"
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>
      <Text v-if="showPercentage" variant="caption" class="text-center mt-1">{{ progress }}%</Text>
    </div>

    <!-- Skeleton Loader -->
    <div v-else-if="variant === 'skeleton'" :class="skeletonClasses"></div>

    <!-- Loading Text -->
    <Text v-if="text" :variant="textVariant" :class="textClasses">
      {{ text }}
    </Text>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Text from './Text.vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'spinner', // spinner, dots, pulse, progress, skeleton
    validator: (value) => ['spinner', 'dots', 'pulse', 'progress', 'skeleton'].includes(value)
  },
  size: {
    type: String,
    default: 'md', // xs, sm, md, lg, xl
    validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value)
  },
  color: {
    type: String,
    default: 'primary', // primary, secondary, success, error, warning, info, white, gray
    validator: (value) => ['primary', 'secondary', 'success', 'error', 'warning', 'info', 'white', 'gray'].includes(value)
  },
  customColor: {
    type: String,
    default: ''
  },
  text: {
    type: String,
    default: ''
  },
  textVariant: {
    type: String,
    default: 'body-sm'
  },
  fullscreen: {
    type: Boolean,
    default: false
  },
  center: {
    type: Boolean,
    default: true
  },
  overlay: {
    type: Boolean,
    default: false
  },
  progress: {
    type: Number,
    default: 0,
    validator: (value) => value >= 0 && value <= 100
  },
  showPercentage: {
    type: Boolean,
    default: false
  },
  ariaLabel: {
    type: String,
    default: 'Loading'
  }
})

const role = computed(() => props.variant === 'progress' ? 'progressbar' : 'status')

const containerClasses = computed(() => {
  const classes = []
  
  if (props.fullscreen) {
    classes.push('fixed inset-0 z-50 flex items-center justify-center')
    if (props.overlay) {
      classes.push('bg-black bg-opacity-50 dark:bg-opacity-70')
    }
  } else if (props.center) {
    classes.push('flex flex-col items-center justify-center')
  }
  
  return classes.join(' ')
})

const colorClasses = computed(() => {
  const colors = {
    primary: 'text-primary-600 dark:text-primary-400',
    secondary: 'text-secondary-600 dark:text-secondary-400',
    success: 'text-green-600 dark:text-green-400',
    error: 'text-red-600 dark:text-red-400',
    warning: 'text-yellow-600 dark:text-yellow-400',
    info: 'text-blue-600 dark:text-blue-400',
    white: 'text-white',
    gray: 'text-gray-600 dark:text-gray-400'
  }
  return colors[props.color] || colors.primary
})

const sizeClasses = computed(() => {
  const sizes = {
    xs: 'h-4 w-4',
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16'
  }
  return sizes[props.size] || sizes.md
})

const spinnerClasses = computed(() => {
  return [colorClasses.value].join(' ')
})

const dotsContainerClasses = computed(() => {
  return 'flex items-center space-x-2'
})

const dotClasses = computed(() => {
  const baseClasses = 'rounded-full animate-bounce'
  const sizeMap = {
    xs: 'h-1 w-1',
    sm: 'h-2 w-2',
    md: 'h-3 w-3',
    lg: 'h-4 w-4',
    xl: 'h-6 w-6'
  }
  return [baseClasses, sizeMap[props.size] || sizeMap.md, colorClasses.value.replace('text-', 'bg-')].join(' ')
})

const pulseClasses = computed(() => {
  const baseClasses = 'rounded-full animate-pulse'
  return [baseClasses, sizeClasses.value, colorClasses.value.replace('text-', 'bg-')].join(' ')
})

const progressBarContainerClasses = computed(() => {
  const heightMap = {
    xs: 'h-1',
    sm: 'h-2',
    md: 'h-3',
    lg: 'h-4',
    xl: 'h-6'
  }
  return ['w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden', heightMap[props.size] || heightMap.md].join(' ')
})

const progressBarClasses = computed(() => {
  const baseClasses = 'h-full transition-all duration-300 ease-in-out'
  const colorMap = {
    primary: 'bg-primary-600',
    secondary: 'bg-secondary-600',
    success: 'bg-green-600',
    error: 'bg-red-600',
    warning: 'bg-yellow-600',
    info: 'bg-blue-600',
    white: 'bg-white',
    gray: 'bg-gray-600'
  }
  return [baseClasses, colorMap[props.color] || colorMap.primary].join(' ')
})

const skeletonClasses = computed(() => {
  const baseClasses = 'animate-pulse bg-gray-200 dark:bg-gray-700 rounded'
  const heightMap = {
    xs: 'h-3',
    sm: 'h-4',
    md: 'h-6',
    lg: 'h-8',
    xl: 'h-12'
  }
  return [baseClasses, 'w-full', heightMap[props.size] || heightMap.md].join(' ')
})

const textClasses = computed(() => {
  return [
    props.center ? 'text-center' : '',
    'mt-2',
    colorClasses.value
  ].filter(Boolean).join(' ')
})
</script>

